import type {
  IDateIntervalInput,
  IDeliveryHierarchyParentInput,
  ILocationBase,
} from '~/client/graph/types.generated'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

type SensorResourceDto = ILocationBase & {
  readonly infuseId?: string | null
  readonly linkedEquipment?: {
    readonly id?: string | null
    readonly name: string
    readonly data?: unknown
    readonly color: string
    readonly iconName: string
  } | null
}

interface SensorLinkedEquipment {
  readonly id: string
  readonly name: string
  readonly data: Readonly<Record<string, unknown>>
  readonly color: string
  readonly iconName: string
}

export interface SensorResource {
  readonly id: string
  readonly name: string
  readonly isDeleted: boolean
  readonly code: string | null
  readonly stxResourceId: string | null
  readonly infuseId: string | null
  readonly templateId: string | null
  readonly data: Readonly<Record<string, unknown>>
  readonly color: string
  readonly iconName: string
  readonly permittedCompanies: readonly string[]
  readonly maxOverlappingDeliveries: number | null
  readonly maxBookingDuration: number | null
  readonly assignedSitemaps: readonly (string | null)[]
  readonly assignedGlobes: readonly (string | null)[]
  readonly operatingIntervals: readonly IDateIntervalInput[]
  readonly parent: IDeliveryHierarchyParentInput | null
  readonly linkedEquipment: SensorLinkedEquipment | null
}

export interface MissingSensorResourceId {
  readonly _tag: 'MissingSensorResourceId'
}

const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...value }
    : {}

const idsOf = (values: readonly unknown[] | null | undefined): string[] =>
  (values ?? [])
    .filter(value => value !== null && value !== undefined)
    .map(String)

const nullableIdsOf = (
  values: readonly (string | null)[] | null | undefined,
): Array<string | null> => [...(values ?? [])]

const linkedEquipmentOf = (
  equipment: SensorResourceDto['linkedEquipment'],
): SensorLinkedEquipment | null => {
  if (!equipment?.id) return null
  return {
    id: String(equipment.id),
    name: equipment.name,
    data: recordOf(equipment.data),
    color: equipment.color,
    iconName: equipment.iconName,
  }
}

export function parseSensorResource(
  location: SensorResourceDto,
): ResourceResult<SensorResource, MissingSensorResourceId> {
  if (location.id === null || location.id === undefined) {
    return resourceErr({ _tag: 'MissingSensorResourceId' })
  }

  return resourceOk({
    id: String(location.id),
    name: location.name,
    isDeleted: !!location.isDeleted,
    code: location.code ?? null,
    stxResourceId: location.stxResourceId ?? null,
    infuseId: location.infuseId ?? null,
    templateId: location.templateId ?? null,
    data: recordOf(location.data),
    color: location.color,
    iconName: location.iconName ?? 'sensor',
    permittedCompanies: idsOf(location.permittedCompanies),
    maxOverlappingDeliveries: location.maxOverlappingDeliveries ?? null,
    maxBookingDuration: location.maxBookingDuration ?? null,
    assignedSitemaps: nullableIdsOf(location.assignedSitemaps),
    assignedGlobes: nullableIdsOf(location.assignedGlobes),
    operatingIntervals: (location.operatingIntervals ?? []).flatMap(interval =>
      interval ? [{ ...interval }] : [],
    ),
    parent: location.parent
      ? {
          parentId:
            location.parent.parentId === null ||
            location.parent.parentId === undefined
              ? null
              : String(location.parent.parentId),
        }
      : null,
    linkedEquipment: linkedEquipmentOf(location.linkedEquipment),
  })
}

export function toSensorResourceRecord(
  resource: SensorResource,
): IResourceRecord {
  const rootFields = {
    id: resource.id,
    name: resource.name,
    code: resource.code,
    stxResourceId: resource.stxResourceId,
    infuseId: resource.infuseId,
    templateId: resource.templateId,
    color: resource.color,
    iconName: resource.iconName,
    parent: resource.parent,
  }

  return {
    ...rootFields,
    isDeleted: resource.isDeleted,
    data: { ...resource.data },
    rollups: {},
    root: {
      ...rootFields,
      linkedEquipment: resource.linkedEquipment,
    },
  }
}
